import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, MessageCircle, RefreshCw, FileText, CheckCircle2, Save, Loader2, List, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
const SERVICE_TYPES = ['Menú Digital Fase 1', 'Menú Digital Fase 2', 'Menú Digital Completo', 'Landing Page', 'KDS Sistema Cocina', 'Delivery System', 'Personalizado'];
const PAYMENT_METHODS = ['SINPE Móvil', 'Transferencia Bancaria', 'Efectivo', 'Otro'];
const PAYMENT_TYPES = ['Pago Total', 'Primer Abono (50%)', 'Segundo Abono (50%)', 'Abono Parcial'];
const DEFAULT_DESCRIPTIONS: Record<string, string> = {
  'Menú Digital Fase 1': 'Implementación de Menú Digital Fase 1. Incluye configuración inicial, carga de menú, y código QR.',
  'Menú Digital Fase 2': 'Implementación de Menú Digital Fase 2. Incluye sistema de pedidos, integración con KDS y reportes básicos.',
  'Menú Digital Completo': 'Sistema integral de Menú Digital. Incluye pedidos, KDS, reportes avanzados y módulo de lealtad.',
  'Landing Page': 'Diseño y desarrollo de Landing Page personalizada con integración al menú digital.',
  'KDS Sistema Cocina': 'Implementación de Kitchen Display System (KDS) para gestión de comandas en cocina.',
  'Delivery System': 'Módulo completo de delivery con asignación de choferes y tracking de órdenes.',
  Personalizado: 'Servicios de desarrollo y configuración personalizados.'
};
export function ReceiptGeneratorPage() {
  const navigate = useNavigate();
  const getNextReceiptNumber = async (): Promise<string> => {
    const year = new Date().getFullYear();
    const prefix = `NOIZA-${year}-`;
    try {
      const {
        data
      } = await supabase.from('noiza_receipts').select('receipt_number').like('receipt_number', `${prefix}%`).order('receipt_number', {
        ascending: false
      }).limit(1);
      if (data && data.length > 0) {
        const lastNum = parseInt(data[0].receipt_number.replace(prefix, ''), 10);
        return `${prefix}${String((lastNum || 0) + 1).padStart(4, '0')}`;
      }
      return `${prefix}0001`;
    } catch {
      return `${prefix}0001`;
    }
  };
  const [formData, setFormData] = useState({
    receiptNumber: '',
    date: new Date().toISOString().split('T')[0],
    clientName: '',
    clientId: '',
    clientPhone: '',
    serviceType: 'Menú Digital Fase 1',
    serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
    totalAmount: 100000,
    paymentAmount: 50000,
    paymentMethod: 'SINPE Móvil',
    paymentReference: '',
    paymentType: 'Primer Abono (50%)',
    notes: ''
  });
  const [savedReceipts, setSavedReceipts] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const remainingBalance = formData.totalAmount - formData.paymentAmount;
  useEffect(() => {
    fetchReceipts();
    // Set initial sequential receipt number
    getNextReceiptNumber().then(num => {
      setFormData(prev => ({
        ...prev,
        receiptNumber: num
      }));
    });
  }, []);
  const fetchReceipts = async () => {
    setLoadingReceipts(true);
    try {
      const {
        data,
        error
      } = await supabase.from('noiza_receipts').select('*').order('created_at', {
        ascending: false
      }).limit(20);
      if (error) throw error;
      if (data) setSavedReceipts(data);
    } catch (err) {
      console.error('Error fetching receipts:', err);
    } finally {
      setLoadingReceipts(false);
    }
  };
  const handleSave = async () => {
    if (!formData.clientName) {
      alert('Por favor ingresa el nombre del cliente');
      return;
    }
    setSaving(true);
    try {
      // Check if receipt number already exists
      const {
        data: existing
      } = await supabase.from('noiza_receipts').select('id').eq('receipt_number', formData.receiptNumber).single();
      const payload = {
        receipt_number: formData.receiptNumber,
        receipt_date: formData.date,
        client_name: formData.clientName,
        client_id: formData.clientId,
        client_phone: formData.clientPhone,
        service_type: formData.serviceType,
        service_description: formData.serviceDescription,
        total_amount: formData.totalAmount,
        payment_amount: formData.paymentAmount,
        remaining_balance: remainingBalance,
        payment_method: formData.paymentMethod,
        payment_reference: formData.paymentReference,
        payment_type: formData.paymentType,
        notes: formData.notes
      };
      let error;
      if (existing) {
        // Update
        const {
          error: updateError
        } = await supabase.from('noiza_receipts').update(payload).eq('id', existing.id);
        error = updateError;
      } else {
        // Insert
        const {
          error: insertError
        } = await supabase.from('noiza_receipts').insert([payload]);
        error = insertError;
      }
      if (error) throw error;
      setSaveSuccess(true);
      fetchReceipts();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving receipt:', err);
      alert('Error al guardar el recibo. Revisa la consola para más detalles.');
    } finally {
      setSaving(false);
    }
  };
  const loadReceipt = (receipt: any) => {
    setFormData({
      receiptNumber: receipt.receipt_number,
      date: receipt.receipt_date,
      clientName: receipt.client_name,
      clientId: receipt.client_id || '',
      clientPhone: receipt.client_phone || '',
      serviceType: receipt.service_type,
      serviceDescription: receipt.service_description || '',
      totalAmount: receipt.total_amount,
      paymentAmount: receipt.payment_amount,
      paymentMethod: receipt.payment_method,
      paymentReference: receipt.payment_reference || '',
      paymentType: receipt.payment_type,
      notes: receipt.notes || ''
    });
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      serviceType: newType,
      serviceDescription: DEFAULT_DESCRIPTIONS[newType] || ''
    }));
  };
  const handleReset = async () => {
    const nextNum = await getNextReceiptNumber();
    setFormData({
      receiptNumber: nextNum,
      date: new Date().toISOString().split('T')[0],
      clientName: '',
      clientId: '',
      clientPhone: '',
      serviceType: 'Menú Digital Fase 1',
      serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
      totalAmount: 100000,
      paymentAmount: 50000,
      paymentMethod: 'SINPE Móvil',
      paymentReference: '',
      paymentType: 'Primer Abono (50%)',
      notes: ''
    });
  };
  const handlePrint = () => {
    window.print();
  };
  const handleWhatsAppShare = () => {
    const text = `*RECIBO DE PAGO - NOIZA* 🧾

*Recibo N°:* ${formData.receiptNumber}
*Fecha:* ${formData.date}
*Cliente:* ${formData.clientName}

*Servicio:* ${formData.serviceType}
*Tipo de Pago:* ${formData.paymentType}
*Método:* ${formData.paymentMethod} ${formData.paymentReference ? `(Ref: ${formData.paymentReference})` : ''}

*Monto Pagado:* ${formatCurrency(formData.paymentAmount)}
*Saldo Pendiente:* ${formatCurrency(remainingBalance)}

Gracias por confiar en NOIZA.
Plataforma de Delivery Digital - Costa Rica
noizacr.com`;
    const encodedText = encodeURIComponent(text);
    let phone = formData.clientPhone.replace(/\D/g, '');
    if (phone && !phone.startsWith('506')) {
      phone = '506' + phone;
    }
    const url = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
    window.open(url, '_blank');
  };
  return <div className="min-h-screen bg-[#0B0B0B] text-white print:bg-white print:text-black" data-id="element-3616">
      {/* Print Styles */}
      <style data-id="element-3617">
        {`
          @media print {
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .receipt-container {
              box-shadow: none !important;
              border: none !important;
              padding: 24px !important;
              margin: 0 auto !important;
              width: 100% !important;
              max-width: 100% !important;
              border-radius: 0 !important;
              background: white !important;
              color: black !important;
            }
            .print-text-black { color: black !important; }
            .print-border-black { border-color: #e5e7eb !important; }
            .print-bg-gray { background-color: #f9fafb !important; }
          }
        `}
      </style>

      {/* Header - Hidden in print */}
      <div className="no-print sticky top-0 z-10 bg-[#0B0B0B]/95 backdrop-blur-md border-b border-white/10 px-4 sm:px-6 py-4" data-id="element-3618">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-3619">
          <div className="flex items-center gap-4" data-id="element-3620">
            <button onClick={() => navigate('/admin/enrollment')} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-id="element-3621">
              <ArrowLeft className="w-5 h-5 text-white/70" data-id="element-3622" />
            </button>
            <div data-id="element-3623">
              <h1 className="text-xl font-bold text-white" data-id="element-3624">
                Generador de Recibos
              </h1>
              <p className="text-sm text-white/50" data-id="element-3625">NOIZA Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-3626">
            <button onClick={handleReset} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-3627">
              <RefreshCw className="w-4 h-4" data-id="element-3628" />
              <span className="hidden sm:inline" data-id="element-3629">Nuevo</span>
            </button>

            <button onClick={handleSave} disabled={saving} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${saveSuccess ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`} data-id="element-3630">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-3631" /> : saveSuccess ? <CheckCircle2 className="w-4 h-4" data-id="element-3632" /> : <Save className="w-4 h-4" data-id="element-3633" />}
              <span className="hidden sm:inline" data-id="element-3634">
                {saving ? 'Guardando...' : saveSuccess ? 'Guardado' : 'Guardar'}
              </span>
            </button>

            <button onClick={handleWhatsAppShare} className="px-4 py-2 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-3635">
              <MessageCircle className="w-4 h-4" data-id="element-3636" />
              <span className="hidden sm:inline" data-id="element-3637">WhatsApp</span>
            </button>
            <button onClick={handlePrint} className="px-4 py-2 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#C9A34E]/90 rounded-lg text-sm font-bold transition-colors flex items-center gap-2" data-id="element-3638">
              <Printer className="w-4 h-4" data-id="element-3639" />
              <span className="hidden sm:inline" data-id="element-3640">Imprimir / PDF</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-3641">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-3642">
          {/* Form Section */}
          <div className="lg:col-span-5 space-y-6 no-print" data-id="element-3643">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6" data-id="element-3644">
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-3645">
                <FileText className="w-5 h-5 text-[#C9A34E]" data-id="element-3646" />
                Datos del Recibo
              </h2>

              <div className="space-y-5" data-id="element-3647">
                <div className="grid grid-cols-2 gap-4" data-id="element-3648">
                  <div data-id="element-3649">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3650">
                      N° de Recibo
                    </label>
                    <input type="text" value={formData.receiptNumber} onChange={e => setFormData({
                    ...formData,
                    receiptNumber: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3651" />
                  </div>
                  <div data-id="element-3652">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3653">
                      Fecha
                    </label>
                    <input type="date" value={formData.date} onChange={e => setFormData({
                    ...formData,
                    date: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3654" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-3655">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-3656">
                    Cliente
                  </h3>
                  <div data-id="element-3657">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3658">
                      Nombre / Razón Social
                    </label>
                    <input type="text" value={formData.clientName} onChange={e => setFormData({
                    ...formData,
                    clientName: e.target.value
                  })} placeholder="Ej. Restaurante El Sabor" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3659" />
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-3660">
                    <div data-id="element-3661">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3662">
                        Cédula (Opcional)
                      </label>
                      <input type="text" value={formData.clientId} onChange={e => setFormData({
                      ...formData,
                      clientId: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3663" />
                    </div>
                    <div data-id="element-3664">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3665">
                        Teléfono / WhatsApp
                      </label>
                      <input type="text" value={formData.clientPhone} onChange={e => setFormData({
                      ...formData,
                      clientPhone: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3666" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-3667">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-3668">
                    Servicio
                  </h3>
                  <div data-id="element-3669">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3670">
                      Tipo de Servicio
                    </label>
                    <select value={formData.serviceType} onChange={handleServiceTypeChange} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3671">
                      {SERVICE_TYPES.map(type => <option key={type} value={type} data-id="element-3672">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-3673">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3674">
                      Descripción
                    </label>
                    <textarea value={formData.serviceDescription} onChange={e => setFormData({
                    ...formData,
                    serviceDescription: e.target.value
                  })} rows={3} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-3675" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-3676">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-3677">
                    Pago
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-3678">
                    <div data-id="element-3679">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3680">
                        Monto Total (Contrato)
                      </label>
                      <div className="relative" data-id="element-3681">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" data-id="element-3682">
                          ₡
                        </span>
                        <input type="number" value={formData.totalAmount} onChange={e => setFormData({
                        ...formData,
                        totalAmount: Number(e.target.value)
                      })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3683" />
                      </div>
                    </div>
                    <div data-id="element-3684">
                      <label className="block text-xs font-medium text-[#C9A34E] mb-1.5" data-id="element-3685">
                        Monto de este Pago
                      </label>
                      <div className="relative" data-id="element-3686">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C9A34E]/60" data-id="element-3687">
                          ₡
                        </span>
                        <input type="number" value={formData.paymentAmount} onChange={e => setFormData({
                        ...formData,
                        paymentAmount: Number(e.target.value)
                      })} className="w-full bg-[#C9A34E]/10 border border-[#C9A34E]/30 rounded-lg pl-8 pr-3 py-2 text-[#C9A34E] font-bold text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3688" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-3689">
                    <div data-id="element-3690">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3691">
                        Tipo de Pago
                      </label>
                      <select value={formData.paymentType} onChange={e => setFormData({
                      ...formData,
                      paymentType: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3692">
                        {PAYMENT_TYPES.map(type => <option key={type} value={type} data-id="element-3693">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-3694">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3695">
                        Método de Pago
                      </label>
                      <select value={formData.paymentMethod} onChange={e => setFormData({
                      ...formData,
                      paymentMethod: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3696">
                        {PAYMENT_METHODS.map(method => <option key={method} value={method} data-id="element-3697">
                            {method}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-3698">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3699">
                      Referencia (Opcional)
                    </label>
                    <input type="text" value={formData.paymentReference} onChange={e => setFormData({
                    ...formData,
                    paymentReference: e.target.value
                  })} placeholder="Ej. Comprobante #123456" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-3700" />
                  </div>

                  <div data-id="element-3701">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-3702">
                      Notas Adicionales
                    </label>
                    <textarea value={formData.notes} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} rows={2} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-3703" />
                  </div>
                </div>
              </div>
            </div>

            {/* Saved Receipts Panel */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden" data-id="element-3704">
              <button onClick={() => setShowSavedList(!showSavedList)} className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors" data-id="element-3705">
                <h2 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-3706">
                  <List className="w-5 h-5 text-blue-400" data-id="element-3707" />
                  Recibos Guardados
                  <span className="bg-white/10 text-white/70 text-xs py-0.5 px-2 rounded-full ml-2" data-id="element-3708">
                    {savedReceipts.length}
                  </span>
                </h2>
                {showSavedList ? <ChevronUp className="w-5 h-5 text-white/50" data-id="element-3709" /> : <ChevronDown className="w-5 h-5 text-white/50" data-id="element-3710" />}
              </button>

              {showSavedList && <div className="border-t border-white/10 p-4 max-h-96 overflow-y-auto custom-scrollbar" data-id="element-3711">
                  {loadingReceipts ? <div className="flex items-center justify-center py-8" data-id="element-3712">
                      <Loader2 className="w-6 h-6 text-white/30 animate-spin" data-id="element-3713" />
                    </div> : savedReceipts.length === 0 ? <div className="text-center py-8 text-white/40 text-sm" data-id="element-3714">
                      No hay recibos guardados aún.
                    </div> : <div className="space-y-2" data-id="element-3715">
                      {savedReceipts.map(receipt => <button key={receipt.id} onClick={() => loadReceipt(receipt)} className="w-full text-left bg-[#0B0B0B] hover:bg-white/5 border border-white/5 rounded-xl p-4 transition-colors group flex items-center justify-between" data-id="element-3716">
                          <div data-id="element-3717">
                            <div className="flex items-center gap-2 mb-1" data-id="element-3718">
                              <span className="text-sm font-bold text-white" data-id="element-3719">
                                {receipt.receipt_number}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-white/10 text-white/70" data-id="element-3720">
                                {receipt.payment_type}
                              </span>
                            </div>
                            <p className="text-sm text-white/70 font-medium" data-id="element-3721">
                              {receipt.client_name}
                            </p>
                            <p className="text-xs text-white/40 mt-1" data-id="element-3722">
                              {new Date(receipt.receipt_date).toLocaleDateString('es-CR')}{' '}
                              • {receipt.service_type}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-3723">
                            <p className="text-sm font-bold text-[#C9A34E]" data-id="element-3724">
                              {formatCurrency(receipt.payment_amount)}
                            </p>
                            <p className="text-xs text-white/40 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3725">
                              Ver recibo →
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>}
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-7 print:col-span-12" data-id="element-3726">
            <div className="sticky top-24 print:static" data-id="element-3727">
              <h2 className="text-sm font-semibold text-white/60 mb-4 uppercase tracking-wider no-print" data-id="element-3728">
                Vista Previa del Recibo
              </h2>

              {/* THE RECEIPT */}
              <div className="receipt-container bg-white text-black rounded-xl p-8 sm:p-12 shadow-2xl shadow-black/50 max-w-2xl mx-auto border border-white/20 print:shadow-none print:border-none print:p-0 print:max-w-full print:rounded-none" data-id="element-3729">
                {/* Header */}
                <div className="flex justify-between items-start mb-10" data-id="element-3730">
                  <div data-id="element-3731">
                    <div className="flex items-center gap-2 mb-2" data-id="element-3732">
                      <div className="w-10 h-10 bg-[#0B0B0B] rounded-lg flex items-center justify-center print-bg-gray" data-id="element-3733">
                        <span className="text-[#C9A34E] font-bold text-2xl leading-none" data-id="element-3734">
                          N
                        </span>
                      </div>
                      <span className="font-bold text-3xl tracking-tight text-[#0B0B0B] print-text-black" style={{
                      fontFamily: "'Playfair Display', serif"
                    }} data-id="element-3735">
                        NOIZA
                      </span>
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-3736">
                      Plataforma de Delivery Digital
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-3737">Costa Rica</p>
                    <p className="text-sm text-gray-500" data-id="element-3738">noizacr.com</p>
                  </div>
                  <div className="text-right" data-id="element-3739">
                    <h2 className="text-3xl font-black text-gray-200 mb-2 uppercase tracking-widest" data-id="element-3740">
                      Recibo
                    </h2>
                    <p className="text-sm font-bold text-gray-800" data-id="element-3741">
                      N° {formData.receiptNumber}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-3742">
                      Fecha:{' '}
                      {new Date(formData.date).toLocaleDateString('es-CR')}
                    </p>
                  </div>
                </div>

                {/* Client Info */}
                <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-100 print-bg-gray print-border-black" data-id="element-3743">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-3744">
                    Recibí de:
                  </h3>
                  <p className="text-lg font-bold text-gray-800" data-id="element-3745">
                    {formData.clientName || 'Nombre del Cliente'}
                  </p>
                  <div className="flex gap-6 mt-1 text-sm text-gray-600" data-id="element-3746">
                    {formData.clientId && <p data-id="element-3747">
                        <span className="font-semibold" data-id="element-3748">Cédula:</span>{' '}
                        {formData.clientId}
                      </p>}
                    {formData.clientPhone && <p data-id="element-3749">
                        <span className="font-semibold" data-id="element-3750">Tel/WA:</span>{' '}
                        {formData.clientPhone}
                      </p>}
                  </div>
                </div>

                {/* Service Details */}
                <div className="mb-8" data-id="element-3751">
                  <table className="w-full text-left border-collapse" data-id="element-3752">
                    <thead data-id="element-3753">
                      <tr className="border-b-2 border-gray-200" data-id="element-3754">
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-3755">
                          Descripción del Servicio
                        </th>
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right" data-id="element-3756">
                          Total Contrato
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3757">
                      <tr className="border-b border-gray-100" data-id="element-3758">
                        <td className="py-4 pr-4" data-id="element-3759">
                          <p className="font-bold text-gray-800 mb-1" data-id="element-3760">
                            {formData.serviceType}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-3761">
                            {formData.serviceDescription}
                          </p>
                        </td>
                        <td className="py-4 text-right font-semibold text-gray-800 align-top" data-id="element-3762">
                          {formatCurrency(formData.totalAmount)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Payment Breakdown */}
                <div className="flex justify-end mb-10" data-id="element-3763">
                  <div className="w-full sm:w-1/2 space-y-3" data-id="element-3764">
                    <div className="flex justify-between text-sm text-gray-600" data-id="element-3765">
                      <span data-id="element-3766">Total del Contrato:</span>
                      <span data-id="element-3767">{formatCurrency(formData.totalAmount)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-[#0B0B0B] border-y border-gray-200 py-3 print-text-black" data-id="element-3768">
                      <span data-id="element-3769">Monto Pagado:</span>
                      <span data-id="element-3770">{formatCurrency(formData.paymentAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium text-gray-500" data-id="element-3771">
                      <span data-id="element-3772">Saldo Pendiente:</span>
                      <span data-id="element-3773">{formatCurrency(remainingBalance)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Info & Notes */}
                <div className="grid grid-cols-2 gap-8 mb-12" data-id="element-3774">
                  <div data-id="element-3775">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-3776">
                      Detalles del Pago
                    </h3>
                    <p className="text-sm text-gray-800" data-id="element-3777">
                      <span className="font-semibold" data-id="element-3778">Tipo:</span>{' '}
                      {formData.paymentType}
                    </p>
                    <p className="text-sm text-gray-800" data-id="element-3779">
                      <span className="font-semibold" data-id="element-3780">Método:</span>{' '}
                      {formData.paymentMethod}
                    </p>
                    {formData.paymentReference && <p className="text-sm text-gray-800" data-id="element-3781">
                        <span className="font-semibold" data-id="element-3782">Referencia:</span>{' '}
                        {formData.paymentReference}
                      </p>}
                  </div>
                  {formData.notes && <div data-id="element-3783">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-3784">
                        Notas
                      </h3>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap" data-id="element-3785">
                        {formData.notes}
                      </p>
                    </div>}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 pt-6 flex justify-between items-end relative" data-id="element-3786">
                  <div data-id="element-3787">
                    <p className="text-xs text-gray-400 font-medium" data-id="element-3788">
                      Este recibo es un comprobante de pago válido.
                    </p>
                    <p className="text-xs text-gray-400 font-medium mt-1" data-id="element-3789">
                      Generado por NOIZA - Plataforma de Delivery Digital
                    </p>
                  </div>

                  {/* Watermark/Stamp */}
                  <div className="absolute right-0 bottom-4 opacity-10 rotate-[-15deg] pointer-events-none" data-id="element-3790">
                    <div className="border-4 border-black rounded-lg px-6 py-2" data-id="element-3791">
                      <span className="text-4xl font-black tracking-widest uppercase" data-id="element-3792">
                        {formData.paymentAmount >= formData.totalAmount ? 'Pagado' : 'Abono'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}